/**
 * The portal's two fetch helpers, shared by the registrar dashboard and the
 * identity-review section (they were duplicated per page).
 *
 * A non-2xx throws an Error carrying `status` and the parsed `data`, which is
 * what `classifyResolveError` in identity-review.ts reads: the worker's 409s
 * carry a body (a fresh candidates payload, or `in_progress`) that the screen
 * must act on, so it can never be thrown away.
 *
 * The CSRF header is exactly `X-Requested-With: XMLHttpRequest` (estate
 * standard — the worker checks that literal value).
 */

export interface ApiError extends Error {
  status: number;
  data: unknown;
}

export async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' });
  // A GET can refuse with a body too: the relink chooser's candidates read
  // answers 409 `already_lodged` with the sentence the registrar must see.
  // Throwing the status alone used to drop it.
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as unknown;
    throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status, data });
  }
  return (await res.json()) as T;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => null)) as T;
  if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status, data });
  return data;
}
